// Publisher-facing onboarding form, reached via a tokenized link a super admin
// generates and emails. No login: the HMAC token in the URL is the capability.
//
// Deliberately a single page with stacked sections, mirroring the internal
// /admin/onboard form rather than the buyer wizard's multi-step flow — a
// publisher fills this in once, often across several sittings, so autosave
// matters more than step gating.
//
// Form v2 (CTV-680 revisions): one name field, the slug reframed as "Your
// station's web address" with a live URL preview, coverage + minimums moved
// admin-side, a required Ad Ops contact, live section completion, and
// "Submit and Onboard Another Station" for org contacts onboarding several
// stations off one link.

import React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Building2, Check, CheckCircle2, DollarSign, Film, Info, Loader2,
  Pencil, Plus, Palette, UsersRound, Wrench, XCircle,
} from 'lucide-react';

import { Section } from '../../components/self-service/admin/fields/FormSection';
import BrandingFields from '../../components/self-service/admin/fields/BrandingFields';
import {
  inputClass, labelClass,
} from '../../components/self-service/admin/fields/publisherFieldStyles';
import { redirectTopPaddingClickToCaretEnd } from '../../components/self-service/admin/fields/caretAtEnd';
import { intakeUploadEndpoints } from '../../components/self-service/admin/uploadEndpoints';
import { useSlugAvailability } from '../../components/self-service/admin/useSlugAvailability';

import IntakeClipUploader from '../../components/self-service/onboarding/IntakeClipUploader';
import TeamMembersFields from '../../components/self-service/onboarding/TeamMembersFields';
import {
  sectionCompletion,
  sectionsWithErrors,
} from '../../components/self-service/onboarding/sectionCompletion';
import { usePublisherIntakeDraft } from '../../components/self-service/onboarding/usePublisherIntakeDraft';
import {
  RECOMMENDED_MIN_CPM,
  formatUsPhone,
  validateIntakePayload,
  type IntakeErrors,
} from '../../components/self-service/onboarding/validateIntakePayload';
import { publisherIntakeService } from '../../services/publisherIntakeService';
import type { IntakeChainStation, IntakeValues } from '../../types/publisherIntake';

const CARD = 'rounded-2xl border p-6 text-center max-w-md mx-auto';

// Always the production address, regardless of where the form itself is
// served: a publisher on a dev or preview deploy should still see the URL
// their advertisers will actually use.
const PROD_HOST = 'streamline.ctvbuyer.com';

const PublisherIntakePage: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const draft = usePublisherIntakeDraft(token);

  // Which finalized chain row's explainer is open (by index), if any.
  const [finalizedNoticeFor, setFinalizedNoticeFor] = React.useState<number | null>(null);

  // Stations already spawned from this link. Lives HERE, above the keyed
  // form, so hopping between stations doesn't reset it — and a failed
  // refresh (e.g. a transient 429 while hopping quickly) keeps the last-good
  // list instead of blanking the sidebar card.
  const [chain, setChain] = React.useState<IntakeChainStation[]>([]);
  const refreshChain = React.useCallback((tok: string) => {
    publisherIntakeService
      .chain(tok)
      .then((res) => { if (res.stations) setChain(res.stations); })
      .catch(() => { /* keep the last-good chain */ });
  }, []);
  React.useEffect(() => {
    if (token) refreshChain(token);
  }, [token, refreshChain]);
  // The link deliberately stays usable after submitting so a publisher can fix
  // a typo, right up until an admin finalizes the station. Without this the
  // thank-you panel would be a dead end despite the API accepting revisions.
  const [editing, setEditing] = React.useState(false);
  // "Onboard another station" from a finalized link (links outlive finalize).
  const [continuing, setContinuing] = React.useState(false);
  const [continueError, setContinueError] = React.useState<string | null>(null);
  // Hopping via the chain card means "I want to edit that station": open the
  // form directly. Without this the thank-you interstitial appeared or not
  // depending on whatever `editing` was left at by the PREVIOUS action.
  const handleHopToStation = (stationToken: string) => {
    setEditing(true);
    navigate(`/onboard/${stationToken}`);
  };

  const handleContinueFromConsumed = async () => {
    if (!token) return;
    setContinuing(true);
    setContinueError(null);
    try {
      const next = await publisherIntakeService.continueIntake(token);
      navigate(`/onboard/${next.token}`);
    } catch (e) {
      setContinueError(e instanceof Error ? e.message : 'Could not start another station.');
    } finally {
      setContinuing(false);
    }
  };

  if (draft.isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="flex items-center gap-2 text-[14px] text-slate-600">
          <Loader2 className="w-5 h-5 animate-spin" />
          Loading your onboarding form…
        </div>
      </div>
    );
  }

  if (draft.loadError) {
    return (
      <Shell>
        <div className={`${CARD} border-rose-200 bg-rose-50`}>
          <XCircle className="w-8 h-8 text-rose-600 mx-auto mb-3" />
          <h1 className="text-[17px] font-semibold text-slate-900">Onboarding link unavailable</h1>
          <p className="mt-2 text-[13px] text-rose-800">{draft.loadError}</p>
          <p className="mt-3 text-[12px] text-slate-600">
            Ask your CTVBuyer contact to send you a new link.
          </p>
        </div>
      </Shell>
    );
  }

  if (draft.status === 'revoked' || draft.status === 'consumed') {
    // A finalized station's link stays a doorway to the publisher's OTHER
    // stations until every one is finalized — the emailed link is often the
    // only URL they kept.
    const liveSiblings = draft.chain.filter((s) => !s.current && s.token);
    return (
      <Shell>
        <div className={`${CARD} border-slate-200 bg-white`}>
          <CheckCircle2 className="w-8 h-8 text-emerald-600 mx-auto mb-3" />
          <h1 className="text-[17px] font-semibold text-slate-900">
            {draft.status === 'consumed' ? 'This station is all set' : 'This link is no longer active'}
          </h1>
          <p className="mt-2 text-[13px] text-slate-600">
            {draft.status === 'consumed'
              ? 'Your station has been set up. Your CTVBuyer contact can tell you what is next.'
              : 'Ask your CTVBuyer contact for a new onboarding link.'}
          </p>
          {draft.status === 'consumed' && (
            <div className="mt-4 pt-4 border-t border-slate-100">
              <button
                type="button"
                disabled={continuing}
                onClick={() => void handleContinueFromConsumed()}
                className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-[13px] font-semibold text-white bg-slate-900 hover:bg-slate-700 disabled:opacity-50 transition-colors"
              >
                {continuing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
                Onboard another station
              </button>
              {continueError && (
                <p className="mt-2 text-[12px] text-rose-600">{continueError}</p>
              )}
            </div>
          )}
          {draft.status === 'consumed' && liveSiblings.length > 0 && (
            <div className="mt-4 pt-4 border-t border-slate-100 text-left">
              <p className="text-[12px] font-medium text-slate-600 text-center">
                You still have stations in progress on this link:
              </p>
              <ul className="mt-2 space-y-1.5">
                {liveSiblings.map((station, i) => (
                  <li key={`${station.token}-${i}`}>
                    <button
                      type="button"
                      onClick={() => handleHopToStation(station.token as string)}
                      className="w-full flex items-center justify-between gap-2 rounded-xl border border-slate-200 px-4 py-2.5 text-left hover:border-slate-900 transition-colors"
                    >
                      <span className="text-[13px] font-medium text-slate-900 truncate">
                        {station.name || station.slug || `Station ${i + 1}`}
                      </span>
                      <span className="shrink-0 text-[10px] font-medium uppercase tracking-wide px-1.5 py-0.5 rounded bg-sky-50 text-sky-700">
                        {station.status === 'submitted' ? 'Submitted' : 'In progress'}
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </Shell>
    );
  }

  if (draft.status === 'submitted' && !editing) {
    return (
      <Shell>
        <div className={`${CARD} border-emerald-200 bg-emerald-50`}>
          <CheckCircle2 className="w-8 h-8 text-emerald-600 mx-auto mb-3" />
          <h1 className="text-[17px] font-semibold text-slate-900">Thanks, we have everything</h1>
          <p className="mt-2 text-[13px] text-emerald-900">
            A CTVBuyer admin will finish setting up your station and email you when it is live.
          </p>
          <p className="mt-4 text-[12px] text-slate-600">
            Spotted a mistake? You can still make changes until we set your station up.
          </p>
          <button
            type="button"
            onClick={() => setEditing(true)}
            className="mt-2 inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-[13px] font-semibold text-slate-700 bg-white border border-slate-300 hover:bg-slate-50 transition-colors"
          >
            <Pencil className="w-3.5 h-3.5" />
            Edit my responses
          </button>
        </div>
      </Shell>
    );
  }

  return (
    <IntakeForm
      key={token}
      token={token as string}
      chain={chain}
      onHopToStation={handleHopToStation}
      finalizedNoticeFor={finalizedNoticeFor}
      onFinalizedRowClick={setFinalizedNoticeFor}
      draft={draft}
      revising={draft.status === 'submitted'}
      onSubmitted={() => {
        setEditing(false);
        if (token) refreshChain(token);
      }}
      // Called BEFORE submit-and-another's submit: the submit flips status to
      // 'submitted', which would otherwise swap in the thank-you panel and
      // unmount the form mid-operation, losing any sibling-mint error with it.
      onPinFormOpen={() => setEditing(true)}
    />
  );
};

const Shell: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="min-h-screen bg-slate-50 py-12 px-4">{children}</div>
);

const IntakeForm: React.FC<{
  token: string;
  /** Chain card data, owned by the parent so it survives token hops. */
  chain: IntakeChainStation[];
  /** Hop to a sibling station, opening its form directly (parent pins the
   *  editing flag so a submitted station skips the thank-you panel). */
  onHopToStation: (token: string) => void;
  /** Index of the finalized row whose explainer is open; parent-owned so it
   *  survives the keyed remount on token hops. */
  finalizedNoticeFor: number | null;
  onFinalizedRowClick: (index: number | null) => void;
  draft: ReturnType<typeof usePublisherIntakeDraft>;
  /** Re-opening an already-submitted form rather than filling a fresh one. */
  revising?: boolean;
  onSubmitted?: () => void;
  onPinFormOpen?: () => void;
}> = ({
  token, chain, onHopToStation, finalizedNoticeFor, onFinalizedRowClick, draft,
  revising = false, onSubmitted, onPinFormOpen,
}) => {
  const navigate = useNavigate();
  // Seeded once from a settled draft — the page gates on isLoading above, so
  // there's no post-mount reset cascade to guard against.
  const [values, setValues] = React.useState<IntakeValues>(draft.initialValues);
  const [errors, setErrors] = React.useState<IntakeErrors>({});
  const [submitting, setSubmitting] = React.useState(false);
  const [submitError, setSubmitError] = React.useState<string | null>(null);
  const summaryRef = React.useRef<HTMLDivElement>(null);

  const endpoints = React.useMemo(() => intakeUploadEndpoints(token), [token]);
  const slug = useSlugAvailability(values.slug);
  // Live feedback for characters that can NEVER be valid (spaces, symbols, a
  // leading dash). Length problems stay quiet until submit so the message
  // doesn't nag while a short name is mid-keystroke. Uppercase isn't flagged:
  // it normalizes to lowercase on save.
  const slugNormalized = values.slug.trim().toLowerCase();
  const slugHasIllegalChars =
    slugNormalized.length > 0
    && (/[^a-z0-9-]/.test(slugNormalized) || slugNormalized.startsWith('-'));


  const complete = sectionCompletion(values, slug.status === 'taken');
  const requiredDone = [
    complete.station, complete.brand, complete.pricing, complete.adops,
  ].filter(Boolean).length;

  const patch = React.useCallback((next: Partial<IntakeValues>) => {
    setValues((prev) => {
      const merged = { ...prev, ...next };
      draft.save(merged);
      return merged;
    });
  }, [draft]);

  // After a blocked submit, keep the error map live: fixing a field clears its
  // message (and the section's red state) immediately instead of at the next
  // submit, where a stale error sat beside the section's DONE badge (CTV-803
  // craft pass). An empty map stays empty, so nothing nags before the first
  // submit attempt.
  React.useEffect(() => {
    setErrors((prev) =>
      Object.keys(prev).length === 0
        ? prev
        : validateIntakePayload(values, slug.status === 'taken'),
    );
  }, [values, slug.status]);

  /** Validate; on failure scroll to the summary and return null. */
  const validateForSubmit = (): IntakeErrors | null => {
    setSubmitError(null);
    const found = validateIntakePayload(values, slug.status === 'taken');
    setErrors(found);
    if (Object.keys(found).length > 0) {
      // One offending section: jump straight to it, error rendered inline.
      // Several: the top summary is the only place that lists them all.
      // Feature-detected because a throw here would abort the handler and
      // leave the button looking dead, which is the exact bug the summary
      // exists to prevent.
      const sections = sectionsWithErrors(found);
      const target =
        sections.length === 1
          ? document.getElementById(`sec-${sections[0]}`)
          : summaryRef.current;
      if (typeof target?.scrollIntoView === 'function') {
        target.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
      return null;
    }
    return found;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (validateForSubmit() === null) return;
    setSubmitting(true);
    try {
      await draft.submit(values);
      onSubmitted?.();
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : 'Could not submit your form.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubmitAndAnother = async () => {
    if (validateForSubmit() === null) return;
    // Keep this form mounted through the whole operation; see onPinFormOpen.
    onPinFormOpen?.();
    setSubmitting(true);
    try {
      await draft.submit(values);
      // A fresh sibling intake for the same organization; the route change
      // remounts everything with a blank form on the new token.
      const next = await publisherIntakeService.continueIntake(token);
      navigate(`/onboard/${next.token}`);
    } catch (err) {
      // The submit itself may have landed; say so rather than implying loss.
      setSubmitError(
        err instanceof Error
          ? `${err.message} If your station was already submitted, ask your CTVBuyer contact for a fresh link for the next one.`
          : 'Could not start another station.',
      );
    } finally {
      setSubmitting(false);
    }
  };

  const err = (key: string) =>
    errors[key] ? (
      <p className="mt-1 text-[11px] text-rose-600">{errors[key]}</p>
    ) : null;

  // Every message, including any whose field has no inline slot. Without this,
  // a validation rule with no matching err() call blocks submit with nothing
  // on screen and the button just looks dead.
  const allErrors = Object.values(errors).filter(Boolean) as string[];

  // The page picks up the publisher's own brand color as they choose it —
  // the same "your page will feel like yours" promise the wizard preview
  // makes, applied to the form itself.
  const brand = /^#(?:[0-9a-f]{3}|[0-9a-f]{6})$/i.test(values.primaryColor.trim())
    ? values.primaryColor.trim()
    : '#0f172a';

  const checklist: Array<{
    id: string; title: string; done: boolean; optional?: boolean;
  }> = [
    { id: 'sec-station', title: 'Your Station or Channel', done: complete.station },
    { id: 'sec-brand', title: 'Your Brand', done: complete.brand },
    { id: 'sec-footage', title: 'Landing Page Footage', done: complete.footage, optional: true },
    { id: 'sec-pricing', title: 'Pricing', done: complete.pricing },
    { id: 'sec-adops', title: 'Ad Ops / Technical Contact', done: complete.adops },
    { id: 'sec-team', title: 'Your Team', done: complete.team, optional: true },
  ];
  const jumpTo = (id: string) => {
    const el = document.getElementById(id);
    if (typeof el?.scrollIntoView === 'function') {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-300 via-slate-200 to-slate-300 py-8 px-4">
      <div className="max-w-6xl mx-auto space-y-5">
        {/* Full-width identity banner; the rail keeps only live progress. */}
        <header
          className="relative rounded-2xl bg-slate-900 px-6 sm:px-8 py-6 text-white overflow-hidden"
          style={{
            backgroundImage:
              `radial-gradient(120% 200% at 100% 0%, ${brand}33, transparent 55%)`,
          }}
        >
          {/* White CTVBuyer mark, top-right so it doesn't fight the org
              eyebrow. Plain image, deliberately not a link off the form. */}
          <img
            src={`${import.meta.env.BASE_URL}ctvbuyer-logo.svg`}
            alt="CTVBuyer"
            className="absolute top-5 right-6 sm:right-8 h-9 w-auto object-contain opacity-90"
          />
          {draft.organizationName && (
            <p className="text-[12px] font-semibold uppercase tracking-[0.14em] text-slate-300 mb-2">
              {draft.organizationName}
            </p>
          )}
          <h1 className="text-[23px] font-semibold tracking-tight leading-snug">
            {revising ? 'Update your self-service station or channel details' : 'Set up your self-service station or channel on CTVBuyer'}
          </h1>
          <p className="mt-1.5 text-[13px] text-slate-300 leading-relaxed">
            {revising
              ? 'These are the responses you sent us. Change anything you need to and resubmit, and the new version replaces the old one.'
              : 'Tell us about your station or channel so we can get it ready for self-serve advertisers. Your responses save as you go, so you can close this and come back.'}
          </p>
        </header>

        <div className="lg:grid lg:grid-cols-[1fr,300px] lg:gap-8 lg:items-start space-y-5 lg:space-y-0">
        {/* Progress rail on the RIGHT like the buyer wizard's summary rail.
            Sticky on desktop; stacks above the form on mobile. */}
        <aside className="space-y-4 lg:sticky lg:top-8 lg:order-2">
          <div className="rounded-2xl border border-slate-200 bg-white p-5"
            style={{ boxShadow: '0 1px 2px rgba(15,23,42,0.08), 0 18px 40px -16px rgba(15,23,42,0.28)' }}
          >
            <p className="text-[12px] font-medium text-slate-600">
              {requiredDone} of 4 required sections complete
            </p>
            <div className="mt-2 h-1.5 rounded-full bg-slate-100 overflow-hidden">
              <div
                className="h-full rounded-full transition-all duration-300"
                style={{ width: `${(requiredDone / 4) * 100}%`, backgroundColor: brand }}
              />
            </div>
            <ul className="mt-4 space-y-1">
              {checklist.map((item) => (
                <li key={item.id}>
                  <button
                    type="button"
                    onClick={() => jumpTo(item.id)}
                    className="w-full flex items-center gap-2.5 rounded-lg px-2 py-1.5 text-left hover:bg-slate-50 transition-colors"
                  >
                    {item.done ? (
                      <span
                        className="w-[18px] h-[18px] rounded-full flex items-center justify-center shrink-0"
                        style={{ backgroundColor: brand }}
                      >
                        <Check className="w-3 h-3 text-white" />
                      </span>
                    ) : (
                      <span className="w-[18px] h-[18px] rounded-full border-2 border-slate-200 shrink-0" />
                    )}
                    <span className={`text-[12.5px] ${item.done ? 'text-slate-900 font-medium' : 'text-slate-600'}`}>
                      {item.title}
                    </span>
                    {item.optional && !item.done && (
                      <span className="ml-auto text-[10px] font-medium uppercase tracking-wide text-slate-400">
                        Optional
                      </span>
                    )}
                  </button>
                </li>
              ))}
            </ul>
            <p className="mt-3 pt-3 border-t border-slate-100 text-[11px] text-slate-400">
              {draft.saving
                ? 'Saving…'
                : draft.lastSavedAt
                  ? 'Saved. You can close this and come back.'
                  : 'Your responses save automatically.'}
            </p>
          </div>

          {chain.length > 1 && (
            <div className="rounded-2xl border border-slate-200 bg-white p-5"
              style={{ boxShadow: '0 1px 2px rgba(15,23,42,0.08), 0 18px 40px -16px rgba(15,23,42,0.28)' }}
            >
              <p className="text-[12px] font-medium text-slate-600">Stations on this link</p>
              <ul className="mt-2.5 space-y-1">
                {chain.map((station, i) => {
                  const label = station.name || station.slug || `Station ${i + 1}`;
                  const finalized = station.status === 'consumed';
                  const statusLabel =
                    station.status === 'draft' ? 'In progress'
                      : station.status === 'submitted' ? 'Submitted'
                        : 'Finalized';
                  const hoppable = !station.current && station.token;
                  return (
                    <li key={`${label}-${i}`}>
                      <button
                        type="button"
                        disabled={station.current}
                        onClick={() => {
                          if (hoppable) {
                            // Park the current form first so hopping away
                            // can't lose an un-debounced edit.
                            void draft.saveNow(values);
                            onHopToStation(station.token as string);
                          } else if (finalized) {
                            // Not inert: explain WHY it can't be opened.
                            onFinalizedRowClick(finalizedNoticeFor === i ? null : i);
                          }
                        }}
                        className={
                          'w-full flex items-center justify-between gap-2 rounded-lg px-2 py-1.5 text-left transition-colors '
                          + (station.current
                            ? 'bg-slate-50'
                            : 'hover:bg-slate-50 cursor-pointer')
                        }
                      >
                        <span className={`text-[12.5px] truncate ${station.current ? 'font-semibold text-slate-900' : 'text-slate-700'}`}>
                          {label}
                          {station.current && (
                            <span className="text-slate-400 font-normal">
                              {revising ? ' (editing)' : ' (this form)'}
                            </span>
                          )}
                        </span>
                        <span
                          className={
                            'shrink-0 text-[10px] font-medium uppercase tracking-wide px-1.5 py-0.5 rounded '
                            + (station.status === 'submitted'
                              ? 'bg-emerald-50 text-emerald-700'
                              : station.status === 'draft'
                                ? 'bg-sky-50 text-sky-700'
                                : 'bg-slate-100 text-slate-500')
                          }
                        >
                          {statusLabel}
                        </span>
                      </button>
                      {finalized && finalizedNoticeFor === i && (
                        <p className="mx-2 mb-1 mt-0.5 rounded-lg bg-slate-50 px-2.5 py-2 text-[11px] leading-relaxed text-slate-500">
                          This station has already been finalized by CTVBuyer.
                          Please contact the CTVBuyer team directly for any
                          additional changes.
                        </p>
                      )}
                    </li>
                  );
                })}
              </ul>
              <p className="mt-2.5 text-[11px] text-slate-400 leading-relaxed">
                Click a station to review or edit its responses. Your work here
                is saved first.
              </p>
            </div>
          )}
        </aside>

        <div className="space-y-5 min-w-0 lg:order-1">
        {submitError && (
          <div className="flex items-start gap-2.5 rounded-xl border border-rose-200 bg-rose-50 px-4 py-3">
            <XCircle className="w-4 h-4 mt-0.5 text-rose-600 shrink-0" />
            <p className="text-[13px] text-rose-800">{submitError}</p>
          </div>
        )}

        <div ref={summaryRef}>
          {allErrors.length > 0 && (
            <div className="flex items-start gap-2.5 rounded-xl border border-rose-200 bg-rose-50 px-4 py-3">
              <XCircle className="w-4 h-4 mt-0.5 text-rose-600 shrink-0" />
              <div>
                <p className="text-[13px] font-semibold text-rose-900">
                  {allErrors.length === 1
                    ? 'One thing needs fixing before you can submit:'
                    : `${allErrors.length} things need fixing before you can submit:`}
                </p>
                <ul className="mt-1 space-y-0.5 text-[12px] text-rose-800">
                  {allErrors.map((message) => <li key={message}>· {message}</li>)}
                </ul>
              </div>
            </div>
          )}
        </div>

        {/* noValidate: validateIntakePayload is the single source of truth, so
            the publisher gets one consistent set of inline messages rather than
            browser tooltips on the fields that happen to carry `required`. */}
        <form
          onSubmit={handleSubmit}
          onMouseDown={redirectTopPaddingClickToCaretEnd}
          noValidate
          className="space-y-5"
        >
          <div id="sec-station" className="scroll-mt-6">
          <Section
            title="Your Station or Channel"
            subtitle="What your station or channel is called, and where advertisers will find it."
            icon={Building2}
            complete={complete.station}
          >
            <div className="space-y-4">
              <div>
                <label className={labelClass} htmlFor="intake-name">Station or channel name</label>
                <input
                  id="intake-name"
                  value={values.name}
                  onChange={(e) => patch({ name: e.target.value })}
                  placeholder="e.g. KEZI 9"
                  className={inputClass}
                />
                {err('name')}
              </div>
              <div>
                <label className={labelClass} htmlFor="intake-slug">
                  Your page on Streamline by CTVBuyer
                </label>
                {/* The URL itself is the field: a fixed prefix with the input
                    embedded, so what they're choosing is unmistakable. */}
                <div className="flex items-center bg-white border border-slate-200 rounded-xl px-4 py-2.5 focus-within:border-slate-900 transition-colors">
                  <span className="font-mono text-[13px] text-slate-500 shrink-0 select-none">
                    {PROD_HOST}/
                  </span>
                  <input
                    id="intake-slug"
                    value={values.slug}
                    onChange={(e) => patch({ slug: e.target.value })}
                    placeholder="your-station"
                    className="flex-1 min-w-0 bg-transparent border-0 p-0 pl-0.5 font-mono text-[13px] text-slate-900 placeholder:text-slate-300 focus:outline-none focus:ring-0"
                  />
                </div>
                <p className="mt-1.5 text-[12px] text-slate-600">
                  This is where advertisers buy from your station or channel. Not your
                  station&rsquo;s website, just the short name for your page on Streamline by CTVBuyer.
                </p>
                <p className="mt-1 text-[11px] text-slate-500">
                  Short and lowercase, like your call sign. Letters, numbers, and dashes only.
                </p>
                {slugHasIllegalChars && (
                  <p className="mt-1 text-[11px] text-rose-600">
                    Only lowercase letters, numbers, and dashes, starting with a
                    letter or number.
                  </p>
                )}
                {slug.status === 'taken' && (
                  <p className="mt-1 text-[11px] text-rose-600">
                    That page name is already taken. Try another.
                  </p>
                )}
                {slug.status === 'available' && (
                  <p className="mt-1 text-[11px] text-emerald-700">
                    {slug.normalized} is available.
                  </p>
                )}
                {err('slug')}
              </div>
            </div>
          </Section>
          </div>

          <div id="sec-brand" className="scroll-mt-6">
          <Section
            title="Your Brand"
            subtitle="Your logo and color theme for the pages advertisers see."
            icon={Palette}
            complete={complete.brand}
          >
            <BrandingFields
              // Fonts are an internal styling knob the admin sets at finalize,
              // so the intake neither collects nor renders them.
              values={{ ...values, fontFamily: '', fontUrl: '' }}
              onChange={(p) => patch(p)}
              showExtractLogo={false}
              // Publishers upload; the raw URL input is an admin affordance,
              // and invert/scale are presentation knobs staff tune at finalize.
              showLogoUrlInput={false}
              showLogoAdjustments={false}
              // Secondary only feeds the creative-slot palette, so asking a
              // publisher for it during onboarding is a decision that changes
              // nothing they can see. Staff can still set it at finalize.
              showSecondaryColor={false}
              endpoints={endpoints}
              channelName={values.name.trim()}
            />
            {err('logoUrl')}
            {err('primaryColor')}
          </Section>
          </div>

          <div id="sec-footage" className="scroll-mt-6">
          <Section
            title="Landing Page Footage"
            subtitle={
              <>
                Optional. Share up to 6 short clips totaling{' '}
                <strong className="font-semibold text-slate-700">at least 24 seconds</strong>,
                and we&rsquo;ll cut them into the video on the TV on your landing page.
              </>
            }
            icon={Film}
            complete={complete.footage}
          >
            <IntakeClipUploader
              clips={values.backgroundClips}
              onChange={(backgroundClips) => patch({ backgroundClips })}
              endpoints={endpoints}
            />
            <p className="mt-3 text-[12px] text-slate-500">
              Just send us the raw footage. CTVBuyer edits it into the final video
              when your station goes live.
            </p>
          </Section>
          </div>

          <div id="sec-pricing" className="scroll-mt-6">
          <Section
            title="Pricing"
            subtitle="What advertisers pay per thousand impressions."
            icon={DollarSign}
            complete={complete.pricing}
          >
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div>
                <label className={labelClass} htmlFor="intake-cpm">CPM ($)</label>
                <input
                  id="intake-cpm"
                  type="number"
                  step="0.01"
                  value={values.ronCpm}
                  onChange={(e) => patch({ ronCpm: e.target.value })}
                  className={inputClass}
                />
                <p className="mt-1 text-[11px] text-slate-500">
                  Your rate per thousand impressions.
                  {' '}
                  <span className="font-medium text-slate-700">
                    Recommended minimum: ${RECOMMENDED_MIN_CPM}.
                  </span>
                </p>
                {err('ronCpm')}
              </div>
            </div>
          </Section>
          </div>

          <div id="sec-adops" className="scroll-mt-6">
          <Section
            title="Ad Ops / Technical Contact"
            subtitle="Who CTVBuyer reaches out to when something needs a technical eye: trafficking, streams, integrations."
            icon={Wrench}
            complete={complete.adops}
          >
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className={labelClass} htmlFor="intake-adops-email">Contact email</label>
                <input
                  id="intake-adops-email"
                  type="email"
                  value={values.adOpsEmail}
                  onChange={(e) => patch({ adOpsEmail: e.target.value })}
                  placeholder="adops@yourstation.com"
                  className={inputClass}
                />
                {err('adOpsEmail')}
              </div>
              <div>
                <label className={labelClass} htmlFor="intake-adops-phone">
                  Phone <span className="font-normal normal-case tracking-normal text-slate-400">(optional)</span>
                </label>
                <input
                  id="intake-adops-phone"
                  type="tel"
                  value={values.adOpsPhone}
                  onChange={(e) => patch({ adOpsPhone: formatUsPhone(e.target.value) })}
                  maxLength={14}
                  placeholder="(555) 555-0100"
                  className={inputClass}
                />
                {err('adOpsPhone')}
              </div>
            </div>
          </Section>
          </div>

          <div id="sec-team" className="scroll-mt-6">
          <Section
            title="Your Team"
            subtitle="Optional. Who at this station or channel will use this product; you can also provide this later."
            icon={UsersRound}
            complete={complete.team}
          >
            <TeamMembersFields
              members={values.teamMembers}
              onChange={(teamMembers) => patch({ teamMembers })}
              errors={errors}
            />

            <div className="mt-5 pt-5 border-t border-slate-100">
              {/* Deliberately NOT one big <label>: wrapping the title and
                  disclaimer made any stray click in the block flip the box. */}
              <div className="flex items-start gap-2.5">
                <input
                  type="checkbox"
                  aria-label="Show your team their notional commission?"
                  checked={values.showTeamCommission}
                  onChange={(e) => patch({ showTeamCommission: e.target.checked })}
                  className="mt-0.5 w-4 h-4 rounded border-slate-300 text-slate-900 focus:ring-slate-900 cursor-pointer"
                />
                <div>
                  <p className="text-[13px] font-medium text-slate-900">
                    Show your team their notional commission?
                  </p>
                  <p className="mt-0.5 text-[12px] text-slate-500 leading-relaxed">
                    A visualization to help your team see the value of what they
                    sell. These are estimates only: not exact figures.
                  </p>
                </div>
              </div>
              {values.showTeamCommission && (
                <div className="mt-3 ml-6 max-w-xs">
                  <label className={labelClass} htmlFor="intake-commission">
                    Station-wide commission % <span className="font-normal normal-case tracking-normal text-slate-400">(optional)</span>
                  </label>
                  <input
                    id="intake-commission"
                    type="number"
                    step="0.1"
                    min={0}
                    max={100}
                    value={values.teamCommissionPercent}
                    onChange={(e) => patch({ teamCommissionPercent: e.target.value })}
                    placeholder="e.g. 12.5"
                    className={inputClass}
                  />
                  {err('teamCommissionPercent')}
                </div>
              )}
              {values.showTeamCommission && (
                <p className="mt-1 ml-6 text-[11px] text-slate-500">
                  Leave blank if it varies by person; rates can be set individually later.
                </p>
              )}
            </div>
          </Section>
          </div>

          <div className="flex items-start gap-2.5 rounded-xl border border-sky-200 bg-sky-50 px-4 py-3">
            <Info className="mt-0.5 h-4 w-4 shrink-0 text-sky-600" />
            <p className="text-[13px] text-sky-900 leading-relaxed">
              Promo Codes and Referral Codes can be created by any manager
              after the station or channel is finalized by CTVBuyer.
            </p>
          </div>

          <div className="flex flex-wrap items-center justify-end gap-3 pt-2">
            <button
              type="button"
              onClick={() => void draft.saveNow(values)}
              className="px-5 py-2.5 rounded-full text-[13px] font-semibold text-slate-700 bg-white border border-slate-300 hover:bg-slate-50 transition-colors"
            >
              Save and finish later
            </button>
            <button
              type="button"
              disabled={submitting}
              onClick={() => void handleSubmitAndAnother()}
              className="inline-flex items-center gap-1.5 px-5 py-2.5 rounded-full text-[13px] font-semibold text-slate-900 bg-white border border-slate-300 hover:border-slate-900 disabled:opacity-50 transition-colors"
            >
              <Plus className="w-4 h-4" />
              Submit and Onboard Another Station
            </button>
            <button
              type="submit"
              disabled={submitting}
              className="inline-flex items-center gap-2 px-6 py-2.5 rounded-full text-[13px] font-semibold text-white hover:opacity-90 disabled:opacity-50 transition-opacity"
              style={{ backgroundColor: brand }}
            >
              {submitting && <Loader2 className="w-4 h-4 animate-spin" />}
              {submitting
                ? (revising ? 'Saving…' : 'Submitting…')
                : (revising ? 'Resubmit' : 'Submit')}
            </button>
          </div>
        </form>

        </div>
        </div>
      </div>
    </div>
  );
};

export default PublisherIntakePage;
